import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, Check, Trash2, ShoppingCart, Package, Apple, Beef, Coffee, RotateCcw, TrendingDown, Store, Loader2, X, ZoomIn, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import AutocompleteInput from "../components/AutocompleteInput";
import ListManager from "../components/ListManager";
import ShareListModal from "../components/ShareListModal";
import { useListContext } from "../contexts/ListContext";
import { useAuth } from "../contexts/AuthContext";
import { toast as sonnerToast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auto-categorization function
const categorizeItem = (itemName) => {
  const name = itemName.toLowerCase();
  
  // ── Brand Recognition (instant) ─────────────────────────────────────
  const brandMap = {
    // Beverages
    "faygo": "Beverages", "coca cola": "Beverages", "pepsi": "Beverages", "dr pepper": "Beverages",
    "mountain dew": "Beverages", "sprite": "Beverages", "fanta": "Beverages", "7up": "Beverages",
    "gatorade": "Beverages", "powerade": "Beverages", "red bull": "Beverages", "monster": "Beverages",
    "celsius": "Beverages", "prime": "Beverages", "body armor": "Beverages", "bodyarmor": "Beverages",
    "snapple": "Beverages", "arizona": "Beverages", "tropicana": "Beverages", "minute maid": "Beverages",
    "simply orange": "Beverages", "ocean spray": "Beverages", "capri sun": "Beverages",
    "kool aid": "Beverages", "kool-aid": "Beverages", "crystal light": "Beverages",
    "starbucks": "Beverages", "dunkin": "Beverages", "folgers": "Beverages", "maxwell house": "Beverages",
    "lipton": "Beverages", "lacroix": "Beverages", "la croix": "Beverages", "perrier": "Beverages",
    "dasani": "Beverages", "fiji": "Beverages", "oatly": "Beverages", "silk": "Beverages",
    "almond breeze": "Beverages", "fairlife": "Beverages", "nesquik": "Beverages",
    "vernors": "Beverages", "sunkist": "Beverages", "shasta": "Beverages", "rc cola": "Beverages",
    // Pet Food
    "nine lives": "Pet Food", "9 lives": "Pet Food", "meow mix": "Pet Food", "fancy feast": "Pet Food",
    "friskies": "Pet Food", "purina": "Pet Food", "iams": "Pet Food", "pedigree": "Pet Food",
    "blue buffalo": "Pet Food", "nutrish": "Pet Food", "beneful": "Pet Food", "alpo": "Pet Food",
    "cesar": "Pet Food", "sheba": "Pet Food", "temptations": "Pet Food", "greenies": "Pet Food",
    "milk-bone": "Pet Food", "milk bone": "Pet Food", "kibbles": "Pet Food",
    "science diet": "Pet Food", "royal canin": "Pet Food", "taste of the wild": "Pet Food",
    "tidy cats": "Pet Food", "fresh step": "Pet Food",
    // Cereal & Breakfast
    "cheerios": "Cereal & Breakfast", "frosted flakes": "Cereal & Breakfast", "froot loops": "Cereal & Breakfast",
    "lucky charms": "Cereal & Breakfast", "cocoa puffs": "Cereal & Breakfast", "trix": "Cereal & Breakfast",
    "cinnamon toast crunch": "Cereal & Breakfast", "cap'n crunch": "Cereal & Breakfast",
    "raisin bran": "Cereal & Breakfast", "special k": "Cereal & Breakfast", "rice krispies": "Cereal & Breakfast",
    "corn flakes": "Cereal & Breakfast", "honey bunches of oats": "Cereal & Breakfast",
    "quaker": "Cereal & Breakfast", "pop tarts": "Cereal & Breakfast", "pop-tarts": "Cereal & Breakfast",
    "eggo": "Cereal & Breakfast", "jimmy dean": "Cereal & Breakfast", "kodiak cakes": "Cereal & Breakfast",
    // Snacks
    "doritos": "Snacks", "cheetos": "Snacks", "fritos": "Snacks", "lays": "Snacks", "lay's": "Snacks",
    "ruffles": "Snacks", "pringles": "Snacks", "tostitos": "Snacks", "sun chips": "Snacks",
    "oreo": "Snacks", "oreos": "Snacks", "chips ahoy": "Snacks", "goldfish": "Snacks",
    "cheez-it": "Snacks", "cheez it": "Snacks", "wheat thins": "Snacks", "ritz": "Snacks",
    "snickers": "Snacks", "m&ms": "Snacks", "reeses": "Snacks", "hershey": "Snacks",
    "kit kat": "Snacks", "twix": "Snacks", "skittles": "Snacks", "kind bar": "Snacks",
    "slim jim": "Snacks", "jack links": "Snacks", "takis": "Snacks", "funyuns": "Snacks",
    "skinny pop": "Snacks", "smartfood": "Snacks",
    // Dairy
    "chobani": "Dairy & Eggs", "yoplait": "Dairy & Eggs", "dannon": "Dairy & Eggs",
    "oikos": "Dairy & Eggs", "fage": "Dairy & Eggs", "sargento": "Dairy & Eggs",
    "tillamook": "Dairy & Eggs", "kerrygold": "Dairy & Eggs", "land o lakes": "Dairy & Eggs",
    "egglands best": "Dairy & Eggs", "vital farms": "Dairy & Eggs", "cool whip": "Dairy & Eggs",
    // Frozen
    "hot pockets": "Frozen", "stouffers": "Frozen", "lean cuisine": "Frozen",
    "digiorno": "Frozen", "red baron": "Frozen", "totinos": "Frozen",
    "ben & jerry's": "Frozen", "haagen-dazs": "Frozen", "talenti": "Frozen", "breyers": "Frozen",
    "birds eye": "Frozen", "ore-ida": "Frozen", "ore ida": "Frozen",
    "uncrustables": "Frozen",
    // Meat brands
    "tyson": "Meat & Seafood", "perdue": "Meat & Seafood", "oscar mayer": "Meat & Seafood",
    "boars head": "Meat & Seafood", "hillshire farm": "Meat & Seafood",
    "johnsonville": "Meat & Seafood", "ball park": "Meat & Seafood", "hebrew national": "Meat & Seafood",
    "hormel": "Meat & Seafood", "spam": "Meat & Seafood", "starkist": "Meat & Seafood",
    "bumble bee": "Meat & Seafood",
    // Bakery brands
    "pepperidge farm": "Bakery", "sara lee": "Bakery", "daves killer bread": "Bakery",
    "natures own": "Bakery", "wonder bread": "Bakery", "thomas": "Bakery",
    "entenmanns": "Bakery", "little debbie": "Bakery", "hostess": "Bakery", "pillsbury": "Bakery",
    // Pantry brands
    "heinz": "Pantry", "hellmanns": "Pantry", "jif": "Pantry", "skippy": "Pantry",
    "nutella": "Pantry", "smuckers": "Pantry", "campbells": "Pantry", "progresso": "Pantry",
    "barilla": "Pantry", "prego": "Pantry", "ragu": "Pantry", "classico": "Pantry",
    "old el paso": "Pantry", "goya": "Pantry", "mccormick": "Pantry",
    // Household
    "tide": "Household", "gain": "Household", "clorox": "Household", "lysol": "Household",
    "dawn": "Household", "bounty": "Household", "charmin": "Household", "swiffer": "Household",
    "glad": "Household", "hefty": "Household", "ziploc": "Household", "febreze": "Household",
    // Health & Personal Care
    "tylenol": "Health & Personal Care", "advil": "Health & Personal Care",
    "colgate": "Health & Personal Care", "crest": "Health & Personal Care",
    "dove": "Health & Personal Care", "old spice": "Health & Personal Care",
    "pantene": "Health & Personal Care", "band-aid": "Health & Personal Care",
    // Baby
    "gerber": "Baby", "similac": "Baby", "enfamil": "Baby", "pampers": "Baby", "huggies": "Baby",
  };
  
  // Check brand matches first
  // Exact match or item contains a known brand (brand must be >4 chars to avoid false positives like milk->milk-bone)
  if (brandMap[name]) return brandMap[name];
  for (const [brand, cat] of Object.entries(brandMap)) {
    if (brand.length > 4 && name.includes(brand)) {
      return cat;
    }
  }

  const categories = {
    "Produce": [
      // Fruits
      "apple", "apples", "banana", "bananas", "orange", "oranges", "grape", "grapes", 
      "strawberry", "strawberries", "blueberry", "blueberries", "raspberry", "raspberries",
      "blackberry", "blackberries", "mango", "mangoes", "pineapple", "kiwi", "kiwis",
      "peach", "peaches", "pear", "pears", "plum", "plums", "cherry", "cherries",
      "watermelon", "cantaloupe", "honeydew", "lemon", "lemons", "lime", "limes", 
      "grapefruit", "avocado", "avocados", "berries", "melons", "citrus", "stone fruit",
      // Vegetables  
      "carrot", "carrots", "broccoli", "spinach", "lettuce", "tomato", "tomatoes", 
      "cucumber", "cucumbers", "bell pepper", "peppers", "onion", "onions", "garlic",
      "potato", "potatoes", "sweet potato", "sweet potatoes", "celery", "corn", 
      "peas", "green beans", "asparagus", "zucchini", "squash", "cabbage", "kale", 
      "brussels sprouts", "cauliflower", "mushroom", "mushrooms", "beets", "radish",
      "eggplant", "artichoke", "leek", "parsnip", "turnip", "greens", "sprouts"
    ],
    "Meat & Seafood": [
      "chicken", "beef", "pork", "turkey", "lamb", "steak", "steaks", "ground beef", 
      "ground turkey", "chicken breast", "chicken thigh", "pork chops", "bacon", 
      "sausage", "sausages", "ham", "deli meat", "hot dogs", "hamburger", "stew meat",
      // Seafood
      "salmon", "tuna", "shrimp", "crab", "fish", "cod", "tilapia", "lobster", 
      "scallops", "mussels", "clams", "sardines", "mackerel", "halibut", "catfish",
      "sea bass", "snapper", "oysters", "calamari", "anchovies", "shellfish", "seafood"
    ],
    "Dairy & Eggs": [
      "milk", "cheese", "yogurt", "butter", "cream", "sour cream", "cottage cheese",
      "mozzarella", "cheddar", "parmesan", "swiss", "eggs", "egg whites", "heavy cream", 
      "half and half", "cream cheese", "ricotta", "feta", "goat cheese", "brie", 
      "american cheese", "string cheese", "whipped cream", "condensed milk", "evaporated milk"
    ],
    "Pantry": [
      "rice", "pasta", "flour", "sugar", "brown sugar", "salt", "pepper", "oil", 
      "olive oil", "vegetable oil", "coconut oil", "vinegar", "balsamic vinegar", 
      "baking soda", "baking powder", "vanilla", "vanilla extract", "spices", "herbs", 
      "quinoa", "oats", "cereal", "granola", "nuts", "almonds", "walnuts", "cashews", "peanuts",
      "beans", "black beans", "kidney beans", "chickpeas", "lentils", "barley",
      // Canned goods
      "canned beans", "canned corn", "canned tomatoes", "tomato sauce", "pasta sauce",
      "applesauce", "soup", "soups", "broth", "stock", "olives", "pickles",
      // Condiments & Sauces
      "ketchup", "mustard", "mayo", "mayonnaise", "peanut butter", "jam", "jelly", 
      "honey", "syrup", "maple syrup", "hot sauce", "bbq sauce", "soy sauce", 
      "worcestershire", "salsa", "ranch", "italian dressing", "salad dressing"
    ],
    "Beverages": [
      "water", "bottled water", "sparkling water", "flavored water", "spring water",
      "juice", "orange juice", "apple juice", "grape juice", "cranberry juice", 
      "vegetable juice", "tomato juice", "lemonade", "fruit punch",
      "soda", "cola", "pepsi", "coke", "sprite", "dr pepper", "root beer", "diet soda",
      "coffee", "ground coffee", "instant coffee", "k cups", "espresso", "cold brew",
      "tea", "green tea", "black tea", "herbal tea", "iced tea", "chai", "matcha",
      "energy drink", "red bull", "monster", "sports drink", "gatorade", "powerade",
      "coconut water", "almond milk", "soy milk", "oat milk", "rice milk", "kombucha",
      "protein shake", "smoothie", "beer", "wine", "champagne", "liquor", "vodka", 
      "whiskey", "rum", "gin", "tequila", "brandy", "cocktail mix", "cider"
    ],
    "Frozen": [
      "frozen vegetables", "frozen fruit", "frozen berries", "ice cream", "frozen pizza", 
      "frozen meals", "frozen dinner", "frozen chicken", "frozen fish", "frozen shrimp",
      "ice", "popsicles", "frozen yogurt", "sorbet", "frozen waffles", "frozen fries",
      "frozen appetizers", "tv dinner", "hot pockets", "frozen burritos"
    ],
    "Bakery": [
      "bread", "white bread", "wheat bread", "whole grain bread", "whole wheat bread", 
      "sourdough", "sourdough bread", "rye bread", "multigrain bread", "sandwich bread",
      "bagels", "english muffins", "muffins", "croissants", "donuts", "danish",
      "cake", "cupcakes", "cookies", "pastry", "pastries", 
      "rolls", "dinner rolls", "hamburger buns", "hot dog buns", "buns", "kaiser rolls",
      "baguette", "pita bread", "naan", "tortillas", "wraps", "flatbread",
      "brioche", "challah", "pumpernickel", "focaccia", "ciabatta"
    ],
    "Snacks": [
      "chips", "potato chips", "tortilla chips", "corn chips", "pretzels", "popcorn", 
      "crackers", "granola bars", "protein bars", "energy bars", "candy", "chocolate", 
      "gum", "mints", "trail mix", "nuts", "dried fruit", "fruit snacks", "cookies", 
      "graham crackers", "animal crackers", "goldfish", "cheetos", "doritos", "fritos",
      "rice cakes", "veggie chips", "beef jerky", "string cheese"
    ]
  };

  // Helper: check if name contains a keyword as a whole word
  const hasWord = (name, word) => {
    const re = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return re.test(name);
  };

  // Items that should go to specific non-food categories (not "Other")
  const specialCategoryKeywords = {
    "Pet Food": ["pet food", "dog food", "cat food", "cat litter", "dog treat", "cat treat", "puppy food", "kitten food"],
    "Baby": ["diapers", "baby wipes", "baby food", "formula", "baby cereal", "sippy cup"],
    "Household": [
      "dish soap", "hand soap", "trash bag", "paper towel", "toilet paper", "tissues",
      "napkins", "aluminum foil", "plastic wrap", "zip lock", "sponge", "bleach",
      "detergent", "fabric softener", "dryer sheets", "air freshener", "candle",
    ],
    "Health & Personal Care": [
      "soap", "body wash", "shampoo", "conditioner", "lotion", "deodorant",
      "toothpaste", "toothbrush", "floss", "mouthwash", "razor", "sunscreen",
      "band aid", "vitamins", "supplements", "medicine", "aspirin", "ibuprofen",
    ],
  };

  // Non-grocery items that map to specific categories
  for (const [cat, keywords] of Object.entries(specialCategoryKeywords)) {
    for (const kw of keywords) {
      if (name.includes(kw)) return cat;
    }
  }

  // Truly uncategorizable non-grocery items
  const otherKeywords = [
    "oil diffuser", "essential oil", "tea tree", "oregano oil", "eucalyptus oil",
    "lavender oil", "peppermint oil", "batteries", "light bulb",
  ];

  for (const kw of otherKeywords) {
    if (name.includes(kw)) return "Other";
  }

  // Check each category for matches with priority ordering
  // First check for exact matches or very specific patterns
  const priorityCategories = ["Bakery", "Beverages", "Meat & Seafood", "Dairy & Eggs", "Produce", "Pantry", "Frozen", "Snacks"];
  
  for (const category of priorityCategories) {
    if (!categories[category]) continue;
    
    for (const item of categories[category]) {
      // Exact match or the grocery item contains the category item as a whole word
      if (name === item || 
          name.includes(` ${item} `) || 
          name.startsWith(`${item} `) || 
          name.endsWith(` ${item}`) ||
          name === item.replace(/s$/, '') || // singular/plural handling
          item === name.replace(/s$/, '')) {
        return category;
      }
    }
  }
  
  // Special handling for high-priority categories first
  
  // Beverages (highest priority) — use whole-word matching to avoid false positives
  if (hasWord(name, "juice") || hasWord(name, "water") || hasWord(name, "coffee") || 
      hasWord(name, "tea") || hasWord(name, "soda") || hasWord(name, "beer") || 
      hasWord(name, "wine") || hasWord(name, "drink") || name === "milk" || 
      hasWord(name, "smoothie") || hasWord(name, "shake") || hasWord(name, "lemonade") ||
      hasWord(name, "kombucha") || hasWord(name, "cider")) {
    return "Beverages";
  }
  
  // Bakery items (high priority to avoid conflicts) — use whole-word matching
  if (hasWord(name, "bread") || hasWord(name, "bun") || hasWord(name, "bagel") || 
      hasWord(name, "muffin") || hasWord(name, "roll") || hasWord(name, "croissant") || 
      hasWord(name, "donut") || hasWord(name, "cake") || hasWord(name, "cookie") || 
      hasWord(name, "pastry") || hasWord(name, "tortilla") || hasWord(name, "naan") || 
      hasWord(name, "pita") || hasWord(name, "sourdough") || hasWord(name, "baguette") || 
      hasWord(name, "ciabatta") || hasWord(name, "focaccia") || hasWord(name, "brioche")) {
    return "Bakery";
  }
  
  // Fallback to broader matching for other categories
  for (const [category, items] of Object.entries(categories)) {
    if (category === "Beverages" || category === "Bakery") continue; // already handled above
    
    if (items.some(item => {
      // For other categories, use contains logic but be more careful
      return name.includes(item) && item.length > 3; // avoid short matches
    })) {
      return category;
    }
  }
  
  return "Other";
};

const Home = () => {
  // Use the ListContext instead of local state
  const {
    lists,
    currentList,
    currentListId,
    handleSelectList,
    handleCreateList,
    handleUpdateList,
    handleDeleteList,
    updateCurrentList
  } = useListContext();
  const { user } = useAuth();
  const [showShareModal, setShowShareModal] = useState(false);
  const [pendingShares, setPendingShares] = useState([]);
  
  const groceryList = currentList?.items || [];

  // Welcome toast for fresh signups (flag set by Auth.jsx after successful register)
  useEffect(() => {
    try {
      if (sessionStorage.getItem("just_signed_up") === "1") {
        sessionStorage.removeItem("just_signed_up");
        sonnerToast.success(
          "Welcome to Foodz Lizts — your 14-day trial just started!",
          { description: "All-Access is unlocked. Cancel anytime." }
        );
      }
    } catch { /* ignore */ }
  }, []);

  // Check for pending shared lists
  useEffect(() => {
    if (!user?.id) return;
    const checkShares = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grocery/share/pending?user_id=${user.id}`);
        if (res.ok) {
          const data = await res.json();
          setPendingShares(data.shares || []);
        }
      } catch { /* ignore */ }
    };
    checkShares();
  }, [user?.id]);

  const acceptShare = async (share) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grocery/share/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ share_id: share.id }),
      });
      if (res.ok) {
        const data = await res.json();
        const newItems = (data.items || []).map((item, idx) => ({
          id: Date.now() + Math.random() + idx,
          name: item.name,
          quantity: item.quantity || 1,
          completed: false,
          category: item.category || "Other",
          estimated_price: item.estimated_price || null,
        }));
        updateCurrentList([...newItems, ...groceryList]);
        setPendingShares((prev) => prev.filter((s) => s.id !== share.id));
      }
    } catch { /* ignore */ }
  };

  const dismissShare = async (shareId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grocery/share/dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ share_id: shareId }),
      });
      setPendingShares((prev) => prev.filter((s) => s.id !== shareId));
    } catch { /* ignore */ }
  };

  // ── Price Intelligence State ──────────────────────────────────────────
  const [marketPrices, setMarketPrices] = useState({});
  const [priceLoading, setPriceLoading] = useState(false);
  const [pricesFetched, setPricesFetched] = useState(false);
  const navigate = useNavigate();

  // Navigate to the deals tab and surface this exact item — Weekly Ads if
  // premium (filtered to the cheapest store + product highlight), Community
  // Deals as a fallback so free users still see relevant deals.
  const openDealsForItem = useCallback((itemName, storeName) => {
    if (!itemName) return;
    const params = new URLSearchParams();
    params.set("item", itemName);
    if (storeName) params.set("store", storeName);
    navigate(`/deals?${params.toString()}`);
  }, [navigate]);

  const fetchPriceIntelligence = useCallback(async (items) => {
    if (!items || items.length === 0) return;
    const itemNames = items.map(i => i.name);
    if (itemNames.length === 0) return;

    setPriceLoading(true);
    try {
      const itemStr = encodeURIComponent(itemNames.join(","));
      const res = await fetch(`${BACKEND_URL}/api/weekly-ads/price-intelligence?items=${itemStr}`);
      if (res.ok) {
        const data = await res.json();
        setMarketPrices(data.prices || {});
      }
    } catch (e) {
      console.error("Price intelligence fetch failed:", e);
    }
    setPriceLoading(false);
    setPricesFetched(true);
  }, []);

  // Fetch prices on initial load and when items change significantly
  useEffect(() => {
    if (groceryList.length > 0) {
      fetchPriceIntelligence(groceryList);
    }
  }, [currentListId]); // eslint-disable-line

  // Parse quantity from input text (e.g. "2 apples" -> quantity: 2, name: "apples")
  const parseQuantityFromInput = (input) => {
    const trimmedInput = input.trim();
    const match = trimmedInput.match(/^(\d+)\s+(.+)$/);
    
    if (match) {
      const quantity = parseInt(match[1], 10);
      const itemName = match[2];
      return { quantity, itemName };
    }
    
    return { quantity: 1, itemName: trimmedInput };
  };

  const addItem = (itemName) => {
    if (itemName && itemName.trim()) {
      const { quantity, itemName: parsedName } = parseQuantityFromInput(itemName.trim());
      
      // Title Case: capitalize first letter of each word
      const titleCased = parsedName.replace(/\b\w/g, (c) => c.toUpperCase());
      
      // Generate estimated price based on item category and name
      const category = categorizeItem(titleCased);
      const estimatedPrice = generateEstimatedPrice(titleCased, category);
      
      const itemId = Date.now();
      const item = {
        id: itemId,
        name: titleCased,
        quantity: quantity,
        completed: false,
        category: category,
        estimated_price: estimatedPrice
      };
      const newList = [item, ...groceryList];
      updateCurrentList(newList);

      // Fetch real market price for the new item
      fetchPriceIntelligence([item]);

      // Async: enhance categorization with AI for "Other" or ambiguous items
      if (category === "Other") {
        enhanceCategoryWithAI(itemId, titleCased);
      }
    }
  };

  // Background AI categorization for items that couldn't be categorized locally
  const enhanceCategoryWithAI = async (itemId, itemName) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/grocery/categorize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [itemName], user_id: user?.id || "" }),
      });
      if (res.ok) {
        const data = await res.json();
        const result = (data.results || [])[0];
        if (result && result.category && result.category !== "Other") {
          // Update the item's category in the current list
          const currentItems = currentList?.items || [];
          const updated = currentItems.map(item =>
            item.id === itemId ? { ...item, category: result.category } : item
          );
          updateCurrentList(updated);
        }
      }
    } catch { /* silent — local categorization still works */ }
  };

  // Generate estimated price per unit for items (placeholder for future API integration)
  const generateEstimatedPrice = (itemName, category) => {
    const basePrices = {
      "Produce": 3.50,
      "Meat & Seafood": 8.99,
      "Dairy & Eggs": 4.25,
      "Pantry": 5.75,
      "Beverages": 3.99,
      "Frozen": 6.50,
      "Bakery": 4.99,
      "Snacks": 4.50,
      "Other": 2.99
    };
    
    // Add some variation based on item name length (simple heuristic)
    const basePrice = basePrices[category] || 2.99;
    const variation = (itemName.length % 3) * 0.50 - 0.50; // -0.50 to +1.00 variation
    
    return Math.max(0.99, basePrice + variation);
  };

  const toggleItem = (id) => {
    const newItems = groceryList.map((item) =>
      item.id === id ? { ...item, completed: !item.completed } : item
    );
    updateCurrentList(newItems);
  };

  const deleteItem = (id) => {
    const newItems = groceryList.filter((item) => item.id !== id);
    updateCurrentList(newItems);
  };

  // Update item quantity
  const updateItemQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return; // Prevent quantity from going below 1
    
    const updatedItems = groceryList.map(item => 
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    updateCurrentList(updatedItems);
  };

  // Increment item quantity
  const incrementQuantity = (id) => {
    const item = groceryList.find(item => item.id === id);
    if (item) {
      updateItemQuantity(id, (item.quantity || 1) + 1);
    }
  };

  // Decrement item quantity  
  const decrementQuantity = (id) => {
    const item = groceryList.find(item => item.id === id);
    if (item && (item.quantity || 1) > 1) {
      updateItemQuantity(id, (item.quantity || 1) - 1);
    }
  };

  // Toggle mark all items as completed/uncompleted
  const markAllDone = () => {
    // Check if all items are already completed
    const allCompleted = groceryList.every(item => item.completed);
    
    // Toggle all items to the opposite state
    const updatedItems = groceryList.map(item => ({
      ...item,
      completed: !allCompleted // If all completed, unmark all; if not all completed, mark all
    }));
    updateCurrentList(updatedItems);
  };

  // Remove all completed items — save to purchased database first
  const clearCompleted = async () => {
    const completedItems = groceryList.filter(item => item.completed);
    const activeItems = groceryList.filter(item => !item.completed);

    // Save completed items to database as purchased
    if (completedItems.length > 0) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/grocery/purchased`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: completedItems.map(i => i.name), user_id: user?.id || null })
        });
      } catch (e) {
        console.error('Failed to save purchased items:', e);
      }
    }

    updateCurrentList(activeItems);
  };

  // List Management Functions are now handled by context
  // Remove the duplicate functions since they're in the context

  // AI Integration Function - simplified since multi-list is now handled in Layout
  const handleAIItemAddition = (aiItems) => {
    if (!aiItems || aiItems.length === 0) return;
    
    const newItems = aiItems.map((item, index) => {
      const titleCased = (item.name || "").replace(/\b\w/g, (c) => c.toUpperCase());
      const category = item.category || categorizeItem(titleCased);
      const estimatedPrice = item.estimated_price || generateEstimatedPrice(titleCased, category);
      
      return {
        id: Date.now() + Math.random() + index,
        name: titleCased,
        quantity: item.quantity || 1,
        completed: false,
        category: category,
        estimated_price: estimatedPrice
      };
    });
    
    updateCurrentList([...newItems, ...groceryList]);
  };

  // Listen for AI item additions from Layout component
  useEffect(() => {
    const handleAddAIItems = (event) => {
      handleAIItemAddition(event.detail);
    };

    window.addEventListener('addAIItems', handleAddAIItems);
    
    return () => {
      window.removeEventListener('addAIItems', handleAddAIItems);
    };
  }, [groceryList]); // Re-setup listener when groceryList changes

  const completedCount = groceryList.filter(item => item.completed).length;
  const totalCount = groceryList.length;
  const allCompleted = totalCount > 0 && completedCount === totalCount;
  
  // Calculate total estimated price — deal-selected items use locked price, others use market data
  const getItemPrice = (item) => {
    if (item.deal_selected && item.estimated_price != null) return item.estimated_price;
    const mp = marketPrices[item.name];
    if (mp) return mp.avg_price;
    return item.estimated_price || 2.99;
  };

  const totalEstimatedPrice = groceryList.reduce((total, item) => {
    const itemPrice = getItemPrice(item) * (item.quantity || 1);
    return total + itemPrice;
  }, 0);

  const marketMatchCount = groceryList.filter(i => marketPrices[i.name] || i.deal_selected).length;

  // Image enlarge state
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [groupBy, setGroupBy] = useState("category"); // "category" or "store"
  
  const getColorConfig = (colorValue) => {
    const colorOptions = {
      blue: { bg: "bg-blue-500", from: "from-blue-500", to: "to-blue-600" },
      green: { bg: "bg-green-500", from: "from-green-500", to: "to-green-600" },
      orange: { bg: "bg-orange-500", from: "from-orange-500", to: "to-orange-600" },
      purple: { bg: "bg-purple-500", from: "from-purple-500", to: "to-purple-600" },
      pink: { bg: "bg-pink-500", from: "from-pink-500", to: "to-pink-600" },
      red: { bg: "bg-red-500", from: "from-red-500", to: "to-red-600" },
      indigo: { bg: "bg-indigo-500", from: "from-indigo-500", to: "to-indigo-600" },
      gray: { bg: "bg-gray-500", from: "from-gray-500", to: "to-gray-600" }
    };
    return colorOptions[colorValue] || colorOptions.green;
  };

  // Group items by category
  const groupedByCategory = groceryList.reduce((groups, item) => {
    const category = item.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {});

  // Group items by store, then by category within each store
  const groupedByStore = {};
  groceryList.forEach((item) => {
    const store = item.store && item.deal_selected ? item.store : "My Items";
    if (!groupedByStore[store]) groupedByStore[store] = {};
    const cat = item.category || "Other";
    if (!groupedByStore[store][cat]) groupedByStore[store][cat] = [];
    groupedByStore[store][cat].push(item);
  });

  const groupedItems = groupBy === "store" ? null : groupedByCategory;

  // Category icons and colors
  const categoryConfig = {
    "Produce": { icon: Apple, color: "bg-green-100 text-green-700 border-green-200" },
    "Meat & Seafood": { icon: Beef, color: "bg-red-100 text-red-800 border-red-200" },
    "Dairy & Eggs": { icon: Package, color: "bg-blue-100 text-blue-700 border-blue-200" },
    "Pantry": { icon: Package, color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
    "Beverages": { icon: Coffee, color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
    "Frozen": { icon: Package, color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
    "Bakery": { icon: Package, color: "bg-orange-100 text-orange-700 border-orange-200" },
    "Snacks": { icon: Package, color: "bg-purple-100 text-purple-700 border-purple-200" },
    "Cereal & Breakfast": { icon: Package, color: "bg-amber-100 text-amber-700 border-amber-200" },
    "Pet Food": { icon: Package, color: "bg-teal-100 text-teal-700 border-teal-200" },
    "Baby": { icon: Package, color: "bg-pink-100 text-pink-700 border-pink-200" },
    "Household": { icon: Package, color: "bg-slate-100 text-slate-700 border-slate-200" },
    "Health & Personal Care": { icon: Package, color: "bg-rose-100 text-rose-700 border-rose-200" },
    "Other": { icon: Package, color: "bg-gray-100 text-gray-700 border-gray-200" }
  };

  const renderItem = (item) => {
    const isDealSelected = !!item.deal_selected;
    const mp = !isDealSelected ? marketPrices[item.name] : null;
    const unitPrice = isDealSelected ? (item.estimated_price || 0) : (mp ? mp.avg_price : (item.estimated_price || 2.99));
    const totalPrice = unitPrice * (item.quantity || 1);
    const hasMarketData = !!mp || isDealSelected;

    return (
      <div key={item.id} className={`p-3 transition-all duration-200 ${item.completed ? "bg-gray-50" : "bg-white"}`}>
        <div className="flex items-start gap-2.5">
          <Checkbox
            checked={item.completed}
            onCheckedChange={() => toggleItem(item.id)}
            className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500 mt-0.5 flex-shrink-0"
          />
          {item.image_url ? (
            <button
              className="w-10 h-10 rounded-lg bg-gray-50 flex-shrink-0 overflow-hidden border border-gray-100 hover:border-blue-300 hover:shadow-sm transition-all relative group"
              onClick={() => setEnlargedImage({ url: item.image_url, name: item.name })}
              data-testid={`item-img-${item.id}`}
            >
              <img src={item.image_url} alt={item.name} className="w-full h-full object-contain" onError={(e) => { e.target.parentElement.style.display = "none"; }} />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <ZoomIn className="h-3 w-3 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
              </div>
            </button>
          ) : null}
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium leading-tight ${
              item.completed ? "line-through text-gray-400"
                : item.source === 'meal-plan' ? "text-amber-800"
                : item.source === 'recipe-suggestion' ? "text-purple-800"
                : "text-gray-900"
            }`}>{item.name}</p>
            {item.source === 'meal-plan' && !isDealSelected && (
              <span className="text-xs bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-0.5 mt-1" data-testid={`source-label-${item.id}`}>from Meal Plan</span>
            )}
            {item.source === 'recipe-suggestion' && !isDealSelected && (
              <span className="text-xs bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-0.5 mt-1" data-testid={`source-label-${item.id}`}>from Recipe</span>
            )}
            {isDealSelected && (
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {item.store && (
                  <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-0.5" data-testid={`item-store-${item.id}`}>
                    <Store className="h-2.5 w-2.5" /> {item.store}
                  </span>
                )}
                {item.original_name && <span className="text-xs text-gray-400 italic">was: {item.original_name}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-2 pl-7">
          <div className="flex items-center gap-1 bg-gray-100 rounded-md px-1.5 py-0.5">
            <Button variant="ghost" size="sm" onClick={() => decrementQuantity(item.id)} className="text-gray-600 hover:text-gray-800 h-6 w-6 p-0" disabled={item.completed || (item.quantity || 1) <= 1}><Minus className="h-3 w-3" /></Button>
            <span className="text-xs font-medium text-gray-700 min-w-[18px] text-center">{item.quantity || 1}</span>
            <Button variant="ghost" size="sm" onClick={() => incrementQuantity(item.id)} className="text-gray-600 hover:text-gray-800 h-6 w-6 p-0" disabled={item.completed}><Plus className="h-3 w-3" /></Button>
          </div>
          <div className="relative group" data-testid={`price-${item.id}`}>
            <button
              type="button"
              onClick={() => {
                if (isDealSelected && item.store) {
                  openDealsForItem(item.original_name || item.name, item.store);
                } else if (mp) {
                  openDealsForItem(item.name, mp.best_store);
                }
              }}
              disabled={!mp && !isDealSelected}
              className={`text-sm font-semibold px-2 py-0.5 rounded-md min-w-[56px] text-center transition-all ${
                isDealSelected
                  ? "bg-green-100 text-green-700 border border-green-300 hover:bg-green-200 cursor-pointer"
                  : hasMarketData
                    ? "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 cursor-pointer"
                    : "bg-gray-100 text-gray-600 cursor-default"
              }`}
              data-testid={`price-pill-btn-${item.id}`}
              aria-label={mp ? `View ad for ${item.name} at ${mp.best_store}` : `Price ${totalPrice.toFixed(2)}`}
            >
              ${totalPrice.toFixed(2)}
            </button>
            {mp && !isDealSelected && (
              <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block z-20 w-56 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg" data-testid={`price-tooltip-${item.id}`}>
                <p className="font-semibold text-green-400 mb-1 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Market Price</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openDealsForItem(item.name, mp.best_store); }}
                  className="block w-full text-left hover:bg-gray-800 rounded px-1 py-0.5 -mx-1 transition-colors group/best"
                  data-testid={`price-tooltip-best-${item.id}`}
                >
                  Best: <span className="font-bold underline decoration-dotted">${mp.best_price.toFixed(2)}</span>
                  <span className="text-gray-300"> at </span>
                  <span className="font-medium underline decoration-dotted">{mp.best_store}</span>
                  <span className="text-blue-300 ml-1 group-hover/best:translate-x-0.5 inline-block transition-transform">→</span>
                </button>
                {mp.sources > 1 && <p className="mt-0.5">Range: ${mp.min_price.toFixed(2)} – ${mp.max_price.toFixed(2)}</p>}
                <p className="text-gray-400 mt-0.5">{mp.sources} store{mp.sources > 1 ? "s" : ""} · tap to view ad</p>
                {mp.savings > 0 && <p className="text-amber-400">Save up to ${mp.savings.toFixed(2)} by comparing</p>}
              </div>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => deleteItem(item.id)} className="text-gray-400 hover:text-red-500 h-7 w-7 p-0"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-3 sm:p-4 space-y-6">
      {/* Guest Save-Forever Nudge — dismissed on signup, reappears on session load */}
      {!user && (
        <div
          className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 p-3 flex items-center gap-3 shadow-sm"
          data-testid="guest-save-nudge"
        >
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Your list is saved on this device.</p>
            <p className="text-xs text-gray-600">
              Sign up to save it forever, sync across phones, and unlock meal plans + deal alerts.
            </p>
          </div>
          <a
            href="/auth?mode=signup"
            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
            data-testid="guest-save-nudge-cta"
          >
            Save Forever
          </a>
        </div>
      )}

      {/* List Manager */}
      <ListManager
        lists={lists}
        currentListId={currentListId}
        onSelectList={handleSelectList}
        onCreateList={handleCreateList}
        onUpdateList={handleUpdateList}
        onDeleteList={handleDeleteList}
        onShareList={() => setShowShareModal(true)}
      />

      {/* Pending Shared Lists */}
      {pendingShares.length > 0 && (
        <div className="space-y-2" data-testid="pending-shares">
          {pendingShares.map((share) => (
            <Card key={share.id} className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {share.from_name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{share.from_name} shared "{share.list_name}"</p>
                  <p className="text-xs text-gray-500">{share.items?.length || 0} items</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white h-7 text-xs px-2" onClick={() => acceptShare(share)} data-testid={`accept-share-${share.id}`}>
                    Add
                  </Button>
                  <Button size="sm" variant="ghost" className="text-gray-400 hover:text-gray-600 h-7 px-1.5" onClick={() => dismissShare(share.id)} data-testid={`dismiss-share-${share.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Floating Header Section */}
      <Card className="bg-white shadow-lg border-0 sticky top-24 z-10">
        <CardContent className="p-0">
          {/* Stats Section with dynamic color */}
          <div className={`bg-gradient-to-r ${getColorConfig(currentList?.color).from} ${getColorConfig(currentList?.color).to} text-white p-3 sm:p-4 rounded-t-lg`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm opacity-90">Progress</p>
                <p className="text-2xl font-bold">{completedCount}/{totalCount}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  {currentList?.budget && (
                    <p className="text-xs opacity-75 whitespace-nowrap">Budget: ${currentList.budget}</p>
                  )}
                  <p className="text-xs opacity-75 whitespace-nowrap">
                    Est. Total: ${totalEstimatedPrice.toFixed(2)}
                  </p>
                  {marketMatchCount > 0 && (
                    <p className="text-xs opacity-75 inline-flex items-center gap-0.5 whitespace-nowrap">
                      <TrendingDown className="h-3 w-3" />
                      {marketMatchCount} ad-priced
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <div className="bg-white/20 rounded-full p-2 sm:p-3">
                  <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                {priceLoading && <Loader2 className="h-3 w-3 animate-spin opacity-70" />}
              </div>
            </div>
          </div>

          {/* Add New Item Section */}
          <div className="p-3 sm:p-4 bg-white rounded-b-lg">
            <AutocompleteInput 
              onAddItem={addItem}
              placeholder="Add new grocery item (e.g., bananas, Cheerios, Faygo)..."
              userId={user?.id}
            />
          </div>
        </CardContent>
      </Card>

      {/* Group By Toggle */}
      {groceryList.length > 0 && (
        <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg p-1" data-testid="group-by-toggle">
          <button
            onClick={() => setGroupBy("category")}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
              groupBy === "category"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="group-by-category"
          >
            <Package className="h-3.5 w-3.5" />
            By Category
          </button>
          <button
            onClick={() => setGroupBy("store")}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
              groupBy === "store"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="group-by-store"
          >
            <Store className="h-3.5 w-3.5" />
            By Store
          </button>
        </div>
      )}

      {/* Price Legend */}
      {groceryList.length > 0 && (
        <div className="flex items-center gap-3 px-1 mt-4" data-testid="price-legend">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-green-100 border border-green-300" />
            <span className="text-xs text-gray-500">Ad / online price</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
            <span className="text-xs text-gray-500">Estimated price</span>
          </div>
        </div>
      )}

      {/* Grocery List */}
      <div className="space-y-4 mt-8">
        {groceryList.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Your grocery list is empty</p>
              <p className="text-sm text-gray-400">Add items above to get started</p>
            </CardContent>
          </Card>
        ) : groupBy === "store" ? (
          /* ── Store → Category two-level view ─────────────────── */
          Object.entries(groupedByStore)
            .sort(([a], [b]) => {
              if (a === "My Items") return 1;
              if (b === "My Items") return -1;
              return a.localeCompare(b);
            })
            .map(([storeName, categories]) => {
              const allStoreItems = Object.values(categories).flat();
              const storeTotal = allStoreItems.reduce((sum, i) => sum + (getItemPrice(i) * (i.quantity || 1)), 0);
              const isMyItems = storeName === "My Items";
              const StoreIcon = isMyItems ? ShoppingCart : Store;

              return (
                <Card key={storeName} className="bg-white overflow-hidden" data-testid={`group-${storeName.replace(/\s+/g, "-").toLowerCase()}`}>
                  <CardContent className="p-0">
                    {/* Store Header */}
                    <div className={`flex items-center gap-3 p-4 border-b ${isMyItems ? "bg-gray-100 text-gray-700" : "bg-blue-100 text-blue-700"}`}>
                      <StoreIcon className="h-5 w-5" />
                      <h3 className="font-semibold text-lg">{storeName}</h3>
                      {!isMyItems && (
                        <Badge className="bg-blue-50 text-blue-600 text-xs border border-blue-200">
                          ${storeTotal.toFixed(2)}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="ml-auto">
                        {allStoreItems.length} {allStoreItems.length === 1 ? 'item' : 'items'}
                      </Badge>
                    </div>

                    {/* Category sub-groups within store */}
                    {Object.entries(categories)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([cat, items]) => {
                        const catConfig = categoryConfig[cat] || categoryConfig["Other"];
                        const CatIcon = catConfig.icon;
                        return (
                          <div key={cat}>
                            {/* Category sub-header */}
                            <div className={`flex items-center gap-2 px-4 py-2 border-b ${catConfig.color} bg-opacity-50`} data-testid={`store-cat-${storeName.replace(/\s+/g, "-").toLowerCase()}-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                              <CatIcon className="h-4 w-4 opacity-70" />
                              <span className="text-sm font-medium">{cat}</span>
                              <span className="text-xs opacity-60 ml-auto">{items.length}</span>
                            </div>
                            {/* Items in this category */}
                            <div className="divide-y">
                              {items.map((item) => renderItem(item))}
                            </div>
                          </div>
                        );
                    })}
                  </CardContent>
                </Card>
              );
            })
        ) : (
          /* ── Category flat view ──────────────────────────────── */
          Object.entries(groupedItems)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, items]) => {
            const config = categoryConfig[category] || categoryConfig["Other"];
            const Icon = config.icon;
            
            return (
              <Card key={category} className="bg-white" data-testid={`group-${category.replace(/\s+/g, "-").toLowerCase()}`}>
                <CardContent className="p-0">
                  {/* Category Header */}
                  <div className={`flex items-center gap-3 p-4 border-b ${config.color}`}>
                    <Icon className="h-5 w-5" />
                    <h3 className="font-semibold text-lg">{category}</h3>
                    <Badge variant="secondary" className="ml-auto">
                      {items.length} {items.length === 1 ? 'item' : 'items'}
                    </Badge>
                  </div>
                  
                  {/* Category Items */}
                  <div className="divide-y">
                    {items.map((item) => renderItem(item))}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Quick Actions */}
      {groceryList.length > 0 && (
        <div className="flex gap-2 pt-4">
          <Button 
            onClick={markAllDone}
            variant="outline" 
            className={`flex-1 ${
              allCompleted 
                ? "border-blue-500 text-blue-600 hover:bg-blue-50"
                : "border-green-500 text-green-600 hover:bg-green-50"
            }`}
          >
            {allCompleted ? (
              <>
                <RotateCcw className="h-4 w-4 mr-2" />
                Unmark All
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Mark All Done
              </>
            )}
          </Button>
          <Button 
            onClick={clearCompleted}
            variant="outline" 
            className="flex-1 border-orange-500 text-orange-600 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={groceryList.filter(item => item.completed).length === 0}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Completed
          </Button>
        </div>
      )}

      {/* Image Enlarge Modal */}
      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6"
          onClick={() => setEnlargedImage(null)}
          data-testid="image-enlarge-overlay"
        >
          <div className="relative max-w-lg w-full bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-800 truncate pr-2">{enlargedImage.name}</p>
              <button onClick={() => setEnlargedImage(null)} className="p-1 hover:bg-gray-100 rounded-full" data-testid="close-enlarged-image">
                <X className="h-4 w-4 text-gray-400" />
              </button>
            </div>
            <div className="bg-gray-50 flex items-center justify-center p-8">
              <img
                src={enlargedImage.url}
                alt={enlargedImage.name}
                className="max-w-full max-h-[60vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <ShareListModal
          list={currentList}
          userId={user?.id}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default Home;